import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Shield, Search, Filter, User, Calendar, Activity, 
  LogIn, LogOut, Edit, Trash2, Plus, Eye, Download, Upload,
  ChevronLeft, ChevronRight, RefreshCw, Clock, FileText
} from "lucide-react";
import { toast } from "sonner";

const AuditPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    action: "",
    entity_type: "",
    user_id: "",
    search: "",
    date_from: "",
    date_to: ""
  });
  const [actionTypes, setActionTypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const limit = 25;

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchFilterOptions();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("skip", page * limit);
      params.append("limit", limit);
      if (filters.action) params.append("action", filters.action);
      if (filters.entity_type) params.append("entity_type", filters.entity_type);
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.search) params.append("search", filters.search);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);

      const res = await axios.get(`${API}/audit?${params.toString()}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. Admin or Sales Head only.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/audit/stats?days=7`);
      setStats(res.data);
    } catch (error) {
      console.error("Stats error:", error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [actionsRes, entitiesRes, usersRes] = await Promise.all([
        axios.get(`${API}/audit/actions`),
        axios.get(`${API}/audit/entity-types`),
        axios.get(`${API}/auth/users`)
      ]);
      setActionTypes(actionsRes.data);
      setEntityTypes(entitiesRes.data);
      setUsers(usersRes.data?.users || []);
    } catch (error) {
      console.error("Filter options error:", error);
    }
  };

  const clearFilters = () => {
    setFilters({ action: "", entity_type: "", user_id: "", search: "", date_from: "", date_to: "" });
    setPage(0);
  };

  const getActionIcon = (action) => {
    const icons = {
      login: <LogIn className="w-4 h-4 text-green-600" />,
      logout: <LogOut className="w-4 h-4 text-slate-500" />,
      create: <Plus className="w-4 h-4 text-blue-600" />,
      update: <Edit className="w-4 h-4 text-amber-600" />,
      delete: <Trash2 className="w-4 h-4 text-red-600" />,
      view: <Eye className="w-4 h-4 text-slate-500" />,
      export: <Download className="w-4 h-4 text-purple-600" />,
      import: <Upload className="w-4 h-4 text-indigo-600" />,
      user_signup: <User className="w-4 h-4 text-green-600" />,
    };
    return icons[action] || <Activity className="w-4 h-4 text-slate-400" />;
  };

  const getActionColor = (action) => {
    const colors = {
      login: "bg-green-100 text-green-800",
      logout: "bg-slate-100 text-slate-800",
      create: "bg-blue-100 text-blue-800",
      update: "bg-amber-100 text-amber-800",
      delete: "bg-red-100 text-red-800",
      view: "bg-slate-100 text-slate-600",
      export: "bg-purple-100 text-purple-800",
      import: "bg-indigo-100 text-indigo-800",
      user_signup: "bg-green-100 text-green-800",
    };
    return colors[action] || "bg-slate-100 text-slate-600";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  if (user?.role !== "admin" && user?.role !== "sales_head") {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700">Access Restricted</h2>
            <p className="text-slate-500 mt-2">Audit logs are only accessible to Admin and Sales Head.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">System Audit</h1>
            <p className="text-slate-500 text-sm mt-1">Track all user activity and data changes</p>
          </div>
          <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }} data-testid="refresh-audit-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => { setFilters({ ...filters, action: "" }); setPage(0); }} data-testid="stat-total">
              <CardContent className="p-4">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Total Events (7d)</p>
                <p className="text-3xl font-bold mt-1">{stats.total_logs?.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className={`bg-white border-slate-200 cursor-pointer hover:border-green-300 transition-colors ${filters.action === "login" ? "ring-2 ring-green-400" : ""}`} onClick={() => { setFilters({ ...filters, action: filters.action === "login" ? "" : "login" }); setPage(0); }} data-testid="stat-logins">
              <CardContent className="p-4">
                <p className="text-slate-500 text-xs uppercase tracking-wider">Logins</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.actions?.login || 0}</p>
              </CardContent>
            </Card>
            <Card className={`bg-white border-slate-200 cursor-pointer hover:border-amber-300 transition-colors ${filters.action === "update" ? "ring-2 ring-amber-400" : ""}`} onClick={() => { setFilters({ ...filters, action: filters.action === "update" ? "" : "update" }); setPage(0); }} data-testid="stat-updates">
              <CardContent className="p-4">
                <p className="text-slate-500 text-xs uppercase tracking-wider">Updates</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{stats.actions?.update || 0}</p>
              </CardContent>
            </Card>
            <Card className={`bg-white border-slate-200 cursor-pointer hover:border-blue-300 transition-colors ${filters.action === "create" ? "ring-2 ring-blue-400" : ""}`} onClick={() => { setFilters({ ...filters, action: filters.action === "create" ? "" : "create" }); setPage(0); }} data-testid="stat-creates">
              <CardContent className="p-4">
                <p className="text-slate-500 text-xs uppercase tracking-wider">Creates</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.actions?.create || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Search logs..."
                  value={filters.search}
                  onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(0); }}
                  className="h-9"
                  data-testid="audit-search-input"
                />
              </div>
              <Select value={filters.action || "all"} onValueChange={(v) => { setFilters({ ...filters, action: v === "all" ? "" : v }); setPage(0); }}>
                <SelectTrigger className="w-[140px] h-9" data-testid="audit-action-filter">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.entity_type || "all"} onValueChange={(v) => { setFilters({ ...filters, entity_type: v === "all" ? "" : v }); setPage(0); }}>
                <SelectTrigger className="w-[140px] h-9" data-testid="audit-entity-filter">
                  <SelectValue placeholder="Entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entityTypes.map(e => <SelectItem key={e} value={e}>{e.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.user_id || "all"} onValueChange={(v) => { setFilters({ ...filters, user_id: v === "all" ? "" : v }); setPage(0); }}>
                <SelectTrigger className="w-[160px] h-9" data-testid="audit-user-filter">
                  <SelectValue placeholder="User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => { setFilters({ ...filters, date_from: e.target.value }); setPage(0); }}
                className="w-[140px] h-9"
                placeholder="From"
              />
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => { setFilters({ ...filters, date_to: e.target.value }); setPage(0); }}
                className="w-[140px] h-9"
                placeholder="To"
              />
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center justify-between">
              <span>Audit Logs ({total.toLocaleString()} total)</span>
              <span className="text-xs text-slate-400">
                Page {page + 1} of {Math.ceil(total / limit) || 1}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : logs.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No audit logs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">Timestamp</th>
                      <th className="text-left p-3">Action</th>
                      <th className="text-left p-3">Entity</th>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Description</th>
                      <th className="text-left p-3">Changes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {logs.map((log) => (
                      <tr key={log.audit_id} className="hover:bg-slate-50" data-testid={`audit-row-${log.audit_id}`}>
                        <td className="p-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span className="text-xs">{formatDate(log.created_at)}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={`${getActionColor(log.action)} text-xs font-medium`}>
                            <span className="flex items-center gap-1">
                              {getActionIcon(log.action)}
                              {log.action}
                            </span>
                          </Badge>
                        </td>
                        <td className="p-3">
                          <span className="text-slate-600 capitalize">{log.entity_type}</span>
                          {log.entity_id && (
                            <span className="text-slate-400 text-xs ml-1">({log.entity_id.slice(-8)})</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="text-slate-800 font-medium text-xs">{log.user_name || "System"}</p>
                            <p className="text-slate-400 text-xs">{log.user_email}</p>
                          </div>
                        </td>
                        <td className="p-3 max-w-xs">
                          <p className="text-slate-600 text-xs truncate" title={log.description}>
                            {log.description}
                          </p>
                        </td>
                        <td className="p-3">
                          {log.changes && Object.keys(log.changes).length > 0 ? (
                            <div className="space-y-1">
                              {Object.entries(log.changes).slice(0, 2).map(([field, change]) => (
                                <div key={field} className="text-xs">
                                  <span className="text-slate-500">{field}:</span>
                                  <span className="text-red-500 line-through ml-1">{String(change.old || "-").slice(0, 20)}</span>
                                  <span className="text-green-600 ml-1">{String(change.new || "-").slice(0, 20)}</span>
                                </div>
                              ))}
                              {Object.keys(log.changes).length > 2 && (
                                <span className="text-xs text-slate-400">+{Object.keys(log.changes).length - 2} more</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-slate-100">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <span className="text-sm text-slate-500">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Logins */}
        {stats?.recent_logins?.length > 0 && (
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                <LogIn className="w-4 h-4" /> Recent Login Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.recent_logins.slice(0, 10).map((login, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-50 last:border-0">
                    <div className="flex items-center gap-2">
                      {login.action === "login" ? (
                        <LogIn className="w-4 h-4 text-green-500" />
                      ) : (
                        <LogOut className="w-4 h-4 text-slate-400" />
                      )}
                      <span className="font-medium text-slate-700">{login.user_name}</span>
                      <span className="text-slate-400 text-xs">{login.user_email}</span>
                    </div>
                    <span className="text-xs text-slate-500">{formatDate(login.created_at)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AuditPage;
